import { supabase } from '@/integrations/supabase/client';
import { callList, callSingle } from '@/lib/rpc/facade';
import type { ListResult, SingleResult } from '@/lib/rpc/types';

export interface LiquidationAddress {
  id: string;
  user_id?: string;
  customer_id?: string;
  asset?: string;
  chain?: string;
  network?: string; // Legacy UI compat
  address: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAddressParams {
  asset?: string;
  network?: string; // Maps to chain
  label?: string;
  /** 
   * For liquidation flow: UUID of the payout_route that links to the bank account.
   * Required for auto-payout liquidation addresses.
   */
  payoutRouteId?: string;
  /** Bridge customer ID (optional, will be resolved from auth if not provided) */
  bridgeCustomerId?: string;
  /** @deprecated Use payoutRouteId instead */
  externalAccountId?: string;
  /** Fiat settlement currency (USD, EUR) - optional, defaults to USD */
  currency?: string;
}

// Map UI network names to Bridge chain names
const CHAIN_MAP: Record<string, string> = {
  'ETH': 'ethereum',
  'Ethereum': 'ethereum',
  'MATIC': 'polygon',
  'Polygon': 'polygon',
  'BASE': 'base',
  'Base': 'base',
};

// Map UI asset names to Bridge asset names
const ASSET_MAP: Record<string, string> = {
  'USDC': 'USDC',
  'USDT': 'USDT',
  'usdc': 'USDC',
  'usdt': 'USDT',
};

/**
 * Create or get existing wallet/liquidation address
 * 
 * Two modes:
 * 1. Custodial Wallet (no payoutRouteId): Creates a wallet to hold funds
 * 2. Liquidation Address (with payoutRouteId): Creates address that auto-sends to bank
 */
export async function createOrGetAddress(params?: CreateAddressParams): Promise<SingleResult<LiquidationAddress>> {
  const requestedChain = params?.network || 'ethereum';
  const bridgeChain = CHAIN_MAP[requestedChain] || requestedChain.toLowerCase();
  const bridgeAsset = ASSET_MAP[params?.asset || 'USDC'] || 'USDC';

  // LIQUIDATION FLOW: If payoutRouteId is provided, create liquidation address
  if (params?.payoutRouteId) {
    return createLiquidationAddress({
      chain: bridgeChain,
      asset: bridgeAsset,
      payout_route_id: params.payoutRouteId,
      bridge_customer_id: params.bridgeCustomerId,
      label: params?.label,
      currency: params?.currency
    });
  }

  // LEGACY: Support deprecated externalAccountId (but this won't work correctly!)
  if (params?.externalAccountId) {
    console.warn('[address.ts] externalAccountId is deprecated. Use payoutRouteId for liquidation addresses.');
    return createLiquidationAddress({
      chain: bridgeChain,
      asset: bridgeAsset,
      payout_route_id: params.externalAccountId, // Attempt to use as payout_route_id
      bridge_customer_id: params.bridgeCustomerId,
      label: params?.label,
      currency: params?.currency
    });
  }

  // CUSTODIAL WALLET FLOW: Create a wallet to hold funds
  return callSingle('user_create_custodial_wallet', {
    p_chain: bridgeChain,
    p_label: params?.label || 'Dashboard Wallet'
  });
}

/**
 * Create a Liquidation Address via Edge Function
 * 
 * Liquidation addresses auto-convert crypto to fiat and send to the linked bank account.
 * Requires a valid payout_route_id that points to an external bank account.
 */
async function createLiquidationAddress(params: { 
  chain: string;
  asset: string;
  payout_route_id: string;
  bridge_customer_id?: string;
  label?: string;
  currency?: string;
}): Promise<SingleResult<LiquidationAddress>> {
  try {
    const { data, error } = await supabase.functions.invoke('create-liquidation-address', {
      body: {
        chain: params.chain,
        asset: params.asset,
        payout_route_id: params.payout_route_id,
        // bridge_customer_id will be resolved from auth if not provided
        ...(params.bridge_customer_id && { bridge_customer_id: params.bridge_customer_id }),
        // Optional parameters
        ...(params.label && { label: params.label }),
        ...(params.currency && { currency: params.currency })
      }
    });

    if (error) {
      console.error('[address.ts] Edge function error:', error);
      throw error;
    }

    // Handle edge function response format
    if (data?.error) {
      console.error('[address.ts] Liquidation address creation failed:', data.error);
      throw new Error(data.error);
    }

    return {
      ok: true,
      data: data as LiquidationAddress,
      source: 'edge-function' as const
    };
  } catch (err) {
    console.error('[address.ts] createLiquidationAddress exception:', err);
    return {
      ok: false,
      error: err instanceof Error ? err : new Error('Failed to create liquidation address'),
      source: 'edge-function' as const
    };
  }
}

/**
 * Get all liquidation addresses/wallets for current user
 * TODO: Migrate to user_get_custodial_wallets
 */
export async function getMyAddresses(): Promise<ListResult<LiquidationAddress>> {
  // Temporary: try to fetch new wallets, if empty, fetch old
  // For now, let's just try to fetch via the generic query or new RPC if available
  // We'll stick to the old one for READs for a moment to not break everything at once,
  // but ideally we need a 'user_get_wallets' RPC.
  return callList('address_get_mine', {});
}
